/**
 * useCurrentUserStore — the central auth + permissions store.
 *
 * Key API surface (all auto-imported, no import needed in components):
 *
 * State:
 *   email, full_name, account_id, account_name, products
 *
 * Getters:
 *   isAdmin           — true if Cognito admin group
 *   originalAccountId — admin's account before impersonation
 *   isImpersonating   — true if admin is acting as another account
 *   has.signal        — boolean, Signal product enabled
 *   has.amplitude     — boolean
 *   has.marketShare   — value
 *   has.customReports — array or null
 *   dashboards        — array of configured dashboards
 *   apis              — array of configured API products
 *
 * Actions:
 *   authenticate()    — refresh tokens; works for both Cognito and WorkOS
 *   token()           — returns the current access token string
 *   fetchAccountDetails() — load full account/product data after sign-in
 *   returnToOriginalAccount() — stop impersonating and reload the app
 *   resetUser()       — clear all state and redirect to sign-in
 *
 * Never access cognito or workos stores directly from components —
 * always go through useCurrentUserStore().
 */
import { defineStore } from 'pinia'
import { useAPI } from '@/composables/useApi'
import { useCognitoStore } from './useCognitoStore'
import { useWorkOSStore } from './useWorkOSStore'
import { useAnalytics } from '@/composables/useAnalytics.js'
import { useServerError } from '@/composables/useServerError'

export const useCurrentUserStore = defineStore('currentUser', {
    state: () => {
        return {
            id: null,
            email: null,
            phone: null,
            is_account_admin: false,
            preferredChallenge: 'EMAIL_OTP',
            full_name: null,
            account_id: null,
            account_name: null,
            account_reg_key: null,
            account_reg_token: null,
            account_seat_count: null,
            account_sso_login_only: false,
            products: null,
            contracts_enabled: null,
            analytics_enabled: null,
            dashboards_enabled: null,
            dashboard_allowed: null,
            dashboards_config: null,
            apis_config: null,
            region_count: null,
            pylon_email_hash: null,
        }
    },
    getters: {
        cognito() {
            return useCognitoStore()
        },
        workos() {
            return useWorkOSStore()
        },
        userId() {
            // June.so doesn't ping us for anonymous user events; an ID has to be supplied. This forces us to create an ID at random
            // for events like user confirmation failure where they don't have a DB ID, just so we can get the event in slack.
            if (this.id) return this.id

            this.$patch({ id: crypto.randomUUID() })

            return this.id
        },
        isAdmin() {
            return this.cognito.isAdmin
        },
        originalAccountId() {
            if (!this.isAdmin || !this.email) return null
            return localStorage.getItem(`serif_original_account_id_${this.email}`)
        },
        isImpersonating() {
            if (!this.originalAccountId || !this.account_id) return false
            return this.originalAccountId !== String(this.account_id)
        },
        has: (state) => {
            return {
                signal: Boolean(state.products?.signal),
                amplitude: Boolean(state.products?.amplitude),
                marketShare: state.products?.marketShare,
                customReports: state.products?.signal?.config?.sigmaReports,
            }
        },
        customReports: (state) => {
            if (!state.products?.signal?.config?.sigmaReports) return null

            const reports = Object.entries(state.products.signal.config.sigmaReports)

            return reports.map(([key, embed]) => {
                return {
                    key,
                    embed,
                    param: new URL(embed).pathname.split('/').pop(),
                }
            })
        },
        dashboards: (state) => state.dashboards_config,
        dashboard: (state) => (dashboard) => state.dashboards_config?.find((c) => c.dashboard === dashboard),
        apis: (state) => state.apis_config,
        isApisProduct() {
            return Boolean(this.apis?.length)
        },
        isDasboardsMenuConfigured() {
            return this.dashboards?.length || this.dashboards_enabled
        },
        isDashboardConfigured() {
            return (dashboard) => this.dashboards?.some((c) => c.dashboard === dashboard)
        },
        defaultDashboard() {
            if (this.dashboards?.length && this.dashboards[0]) {
                return this.dashboards[0].dashboard
            }

            if (this.dashboard_allowed) {
                return this.dashboard_allowed
            }

            return null
        },
    },
    actions: {
        async authenticate() {
            if (this.cognito.token()) return await this.cognito.authenticate()
            return await this.workos.authenticate()
        },
        async authenticateEmbed(apiKey) {
            // try to sign in user with embed credentials

            /// pass that token to the dashboard embed
            const { dashboardAPI } = useAPI()

            let prefs

            try {
                prefs = await dashboardAPI(
                    '/users/embed?' +
                        new URLSearchParams({
                            apiKey,
                        }),
                    {
                        disableTokenRefresh: true,
                    }
                )
            } catch (e) {
                console.error(e)

                await this.resetUser()

                const { getMessage } = useServerError()

                const message = getMessage(e)

                throw new Error(message, { cause: e })
            }

            if (!prefs) {
                await this.resetUser()

                throw new Error('User Embed Prefs Failed')
            }

            const { id, Username, IdToken, AccessToken, RefreshToken, full_name, account, products } = prefs

            try {
                await this.cognito.createUserAndSessionFromTokens({
                    Username,
                    IdToken,
                    AccessToken,
                    RefreshToken,
                })
            } catch (e) {
                console.error('Error creating Session', e)

                throw e
            }

            this.$patch({
                id,
                email: Username,
                full_name,
                account_id: account.id,
                account_name: account.name,
                products,
            })

            const { analytics } = useAnalytics()

            analytics.identify(this.userId, {
                email: Username,
                name: full_name,
                account: account.name,
                account_id: account.id,
            })
        },
        async fetchAccountDetails() {
            const { dashboardAPI } = useAPI()

            let prefs
            try {
                prefs = await dashboardAPI('/users/prefs')
            } catch (e) {
                await this.resetUser()
                const { getMessage } = useServerError()

                const message = getMessage(e)

                throw new Error(message, { cause: e })
            }

            if (!prefs) {
                await this.resetUser()

                throw new Error('User Prefs Failed')
            }

            const { id, email, full_name, is_account_admin, account, products, pylon_email_hash } = prefs

            this.$patch({
                id,
                email,
                full_name,
                is_account_admin: Boolean(is_account_admin),
                account_id: account.id,
                account_name: account.name,
                account_reg_key: account.reg_key ?? null,
                account_reg_token: account.reg_token ?? null,
                account_seat_count: account.seat_count ?? null,
                account_sso_login_only: Boolean(account.sso_login_only),
                products,
                contracts_enabled: account.contracts_enabled,
                analytics_enabled: account.analytics_enabled,
                dashboards_enabled: account.dashboards_enabled,
                dashboard_allowed: account.dashboard_allowed,
                dashboards_config: account.dashboards_config,
                apis_config: account.apis_config,
                region_count: account.region_count,
                pylon_email_hash: pylon_email_hash ?? null,
            })

            // Track original account for admin impersonation detection
            if (this.isAdmin && email) {
                const storageKey = `serif_original_account_id_${email}`
                if (!localStorage.getItem(storageKey)) {
                    localStorage.setItem(storageKey, account.id)
                }
            }

            const { analytics } = useAnalytics()

            analytics.identify(this.userId, {
                email,
                name: full_name,
                account: account.name,
                account_id: account.id,
            })

            if (this.cognito.token()) {
                await this.cognito.setAccountAttribute(account.id)
            }
        },

        async signUp({ email, phone, registrationKey, registrationToken, fullName }) {
            this.cognito.validateEmail(email)
            const { dashboardAPI } = useAPI()
            const normalizedEmail = email.toLowerCase()

            const result = await dashboardAPI('/users', {
                method: 'POST',
                body: {
                    email: normalizedEmail,
                    fullName,
                    phone,
                    regKey: registrationKey,
                    regToken: registrationToken,
                    signupMode: 'password',
                },
                disableTokenRefresh: true,
            })

            this.$patch({
                full_name: fullName,
            })

            const { analytics } = useAnalytics()

            analytics.track({
                userId: this.userId,
                event: 'User Signed Up',
                properties: {
                    regKey: registrationKey,
                    email: normalizedEmail,
                    fullName: fullName,
                },
            })

            return result
        },
        async signUpPasswordless({ email, phone, registrationKey, registrationToken, fullName }) {
            this.cognito.validateEmail(email)
            const { dashboardAPI } = useAPI()
            const normalizedEmail = email.toLowerCase()

            await dashboardAPI('/users', {
                method: 'POST',
                body: {
                    email: normalizedEmail,
                    fullName,
                    phone,
                    regKey: registrationKey,
                    regToken: registrationToken,
                    signupMode: 'passwordless',
                },
                disableTokenRefresh: true,
            })

            this.$patch({
                full_name: fullName,
            })

            const { analytics } = useAnalytics()

            analytics.track({
                userId: this.userId,
                event: 'User Signed Up',
                properties: {
                    regKey: registrationKey,
                    email: normalizedEmail,
                    fullName: fullName,
                },
            })

            return normalizedEmail
        },
        async signIn(payload) {
            await this.cognito.signIn(payload)

            this.$patch({
                email: payload.email,
            })

            const { dashboardAPI } = useAPI()
            try {
                await dashboardAPI('/users/last-login', { method: 'POST' })
            } catch (e) {
                console.warn('Failed to update last_login:', e)
            }

            const { analytics } = useAnalytics()

            analytics.track({
                userId: this.userId,
                event: 'User Signed In',
                properties: {
                    email: payload.email,
                },
            })

            return
        },
        async signInPasswordless({ email, phone, preferredChallenge } = {}) {
            await this.cognito.initiateOtpSignIn({
                email,
                phone,
                preferredChallenge,
            })

            this.$patch({
                email,
                phone,
                preferredChallenge,
            })

            const { analytics } = useAnalytics()

            analytics.track({
                userId: this.userId,
                event: 'User started OTP Sign In',
                properties: {
                    email,
                    phone,
                    preferredChallenge,
                },
            })

            return
        },
        async signOut() {
            await this.resetUser()

            const { analytics } = useAnalytics()

            analytics.track({
                userId: this.userId,
                event: 'User Signed Out',
                properties: {
                    email: this.email,
                    full_name: this.full_name,
                    account: this.account_name,
                },
            })

            return
        },
        async confirmUserPasswordless({ confirmationCode, isSignup = false }) {
            if (isSignup) throw new Error('Signup session expired. Please start signup again.')
            const confirmedEmail = await this.cognito.completeOtpSignIn(confirmationCode)

            const { dashboardAPI } = useAPI()
            await dashboardAPI('/users/confirm', {
                method: 'POST',
                body: { email: confirmedEmail },
                disableTokenRefresh: true,
            })

            const { analytics } = useAnalytics()
            analytics.track({
                event: 'User Confirmed',
                properties: {
                    email: confirmedEmail,
                },
            })

            return confirmedEmail
        },
        async verifySignupEmail(confirmationCode) {
            return this.cognito.completeOtpSignIn(confirmationCode)
        },
        async finalizeSignup({ password } = {}) {
            const email = this.cognito.email
            if (!email) throw new Error('Signup session expired. Please start signup again.')
            if (password) this.cognito.validatePassword(password)
            if (this.cognito.areTokensExpired() && !(await this.cognito.refreshTokens())) {
                throw new Error('Signup session expired. Please start signup again.')
            }

            const { dashboardAPI } = useAPI()
            return dashboardAPI('/users/complete-signup', {
                method: 'POST',
                body: { email, ...(password ? { password } : {}) },
                disableTokenRefresh: true,
            })
        },
        async forgotPassword(payload) {
            const { analytics } = useAnalytics()

            await this.cognito.forgotPassword(payload)

            analytics.track({
                userId: this.userId,
                event: 'User Password Forgot',
                properties: {
                    email: payload.email,
                },
            })

            return
        },
        async resetPassword(payload) {
            const { analytics } = useAnalytics()

            await this.cognito.resetPassword(payload)

            analytics.track({
                userId: this.userId,
                event: 'User Password Reset',
                properties: {
                    email: payload.email,
                },
            })

            return
        },
        token() {
            return this.cognito.token() ?? this.workos.token()
        },
        accessToken() {
            return this.cognito.accessToken()
        },
        areTokensExpired() {
            if (this.cognito.token()) return this.cognito.areTokensExpired()
            return this.workos.areTokensExpired()
        },
        async impersonateAccount(accountId) {
            const { dashboardAPI } = useAPI()
            await dashboardAPI('/users/impersonate', {
                method: 'PATCH',
                body: { account_id: accountId },
            })
        },
        async returnToOriginalAccount({ reload = () => window.location.reload() } = {}) {
            if (!this.isImpersonating) return false

            await this.impersonateAccount(this.originalAccountId)
            reload()
            return true
        },
        async updateSsoLoginOnly(value) {
            const { dashboardAPI } = useAPI()
            const { result } = await dashboardAPI(`/accounts/${this.account_id}`, {
                method: 'PATCH',
                body: { sso_login_only: value },
            })
            this.$patch({ account_sso_login_only: Boolean(result.sso_login_only) })
        },
        async resetUser() {
            await this.cognito.signOut()
            this.workos.signOut()

            this.$reset()
        },
    },
})
